import { useCallback, useRef } from 'react';

import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { PromoFieldConfig } from '../../../../config/tpoGenConfig';
import type { PPGRow } from '../../../../types/promoMetrics';
import { buildOfferPayload } from '../../../../utils/buildOfferPayload';
import { buildAttributesPayload } from '../../../../utils/entityAttributes';
import { TRANSFORMS } from '../../../../utils/buildPromoPayload';
import type { UpliftRequestPayload, UpliftProductPayload, UpliftResponse } from '../mutations/useUpliftMutation';
import {
    TPOGEN_SIMULATE_STARTED,
    TPOGEN_SIMULATE_COMPLETED,
    TPOGEN_SIMULATE_FAILED,
} from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../../utils/trackTpoGenEvent';

type TriggerMode = 'manual' | 'auto';

interface UseUpliftTriggerArgs {
    ppgData: PPGRow[];
    promoFormValues: Record<string, unknown>;
    promoType: string | undefined;
    dim2Attrs: string[];
    planId: string;
    eventId: string;
    promotionId: string | null;
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    mutateAsync: (payload: UpliftRequestPayload) => Promise<UpliftResponse>;
    isPending: boolean;
    /** Engine-derived promo field states (used for offer-completeness visibility). */
    fieldStates: Record<string, { visible: boolean }>;
}

interface UseUpliftTriggerReturn {
    triggerUplift: () => Promise<void>;
    triggerUpliftBeforeSave: () => Promise<boolean>;
    triggerUpliftAuto: () => Promise<void>;
    triggerUpliftDebounced: () => void;
    isUpliftApplicable: boolean;
}

/**
 * Soft filter for auto-trigger path: silently removes PPGs where the
 * filter key (edlp_srp) has no value — no error shown to the user.
 */
function filterPpgsForAutoTrigger(ppgData: PPGRow[], filterKey: string): PPGRow[] {
    return ppgData.filter((ppg) => ppg.values[filterKey] && Number(ppg.values[filterKey]) !== 0);
}

/** Builds the products array with metric name/value pairs for each PPG */
function buildUpliftProducts(ppgData: PPGRow[], payloadMetricKeys: string[]): UpliftProductPayload[] {
    return ppgData.map((ppg) => ({
        dim1_id: ppg.ppg_id,
        metrics: payloadMetricKeys.map((key) => ({
            metric_name: key,
            value: ppg.values[key] ?? 0,
        })),
    }));
}

/**
 * Offer completeness gate: counts expected params for the active sub-method by
 * inspecting offer-param fields that are currently visible (engine-derived), then
 * compares against what buildOfferPayload actually produced.
 */
function isOfferComplete(
    promoFields: PromoFieldConfig[],
    formValues: Record<string, unknown>,
    fieldStates: Record<string, { visible: boolean }>,
): boolean {
    const offerPayload = buildOfferPayload(promoFields, formValues);
    if (!offerPayload) return false;

    let expectedParamCount = 0;

    for (const field of promoFields) {
        if (!field.offerMapping) continue;
        // Only count fields visible for the currently selected sub-method.
        if (!fieldStates[field.name]?.visible) continue;

        const mappings = Array.isArray(field.offerMapping) ? field.offerMapping : [field.offerMapping];
        for (const mapping of mappings) {
            if (mapping.role !== 'param' || !mapping.paramName) continue;
            expectedParamCount++;
            if (mapping.unitParamName) expectedParamCount++;
        }
    }

    return Object.keys(offerPayload.params.params).length >= expectedParamCount;
}

/**
 * Orchestrates uplift validation, payload construction, API trigger, and response
 * consumption. Uses refs for latest args to keep returned functions stable and
 * prevent dependency cycles in consumer effects.
 */
const useUpliftTrigger = ({
    ppgData,
    promoFormValues,
    promoType,
    dim2Attrs,
    planId,
    eventId,
    promotionId,
    onMetricChange,
    mutateAsync,
    isPending,
    fieldStates,
}: UseUpliftTriggerArgs): UseUpliftTriggerReturn => {
    const { upliftConfig, promoFields, entityAttributes } = useTpoGenConfig();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const argsRef = useRef({
        ppgData,
        promoFormValues,
        promoType,
        dim2Attrs,
        planId,
        eventId,
        promotionId,
        onMetricChange,
        mutateAsync,
        isPending,
        upliftConfig,
        promoFields,
        entityAttributes,
        fieldStates,
    });
    argsRef.current = {
        ppgData,
        promoFormValues,
        promoType,
        dim2Attrs,
        planId,
        eventId,
        promotionId,
        onMetricChange,
        mutateAsync,
        isPending,
        upliftConfig,
        promoFields,
        entityAttributes,
        fieldStates,
    };

    // Gate: only applicable for configured promo types (merchandising / sm_mdf)
    const isUpliftApplicable = !!promoType && upliftConfig.applicablePromotionTypes.includes(promoType);

    /** Sets the target uplift metrics per PPG, triggering the KPI cascade engine */
    const applyUpliftResponse = useCallback((data: UpliftResponse) => {
        const { onMetricChange: onChange, upliftConfig: cfg } = argsRef.current;
        for (const item of data.results) {
            const ppgId = String(item.dim1_id);
            for (const [responseKey, feKey] of Object.entries(cfg.targetMetricKeys)) {
                const value = item.metrics[responseKey];
                if (value !== undefined) {
                    onChange(ppgId, feKey, value);
                }
            }
        }
    }, []);

    /** Assembles the full API request payload from PPG data + form values */
    const buildPayload = useCallback((filteredPpgs: PPGRow[]): UpliftRequestPayload | null => {
        const {
            promoFormValues: fv,
            dim2Attrs,
            planId: pid,
            eventId: eid,
            promotionId: promoId,
            upliftConfig: cfg,
            promoFields: fields,
            entityAttributes: attrs,
        } = argsRef.current;
        if (filteredPpgs.length === 0) return null;

        const offerPayload = buildOfferPayload(fields, fv);
        if (!offerPayload) return null;

        const promoAttrs = buildAttributesPayload(attrs, fields, fv, 'promotion', TRANSFORMS);

        for (const { formField, attrKey } of cfg.dateFields) {
            const raw = fv[formField];
            if (raw) {
                const transform = TRANSFORMS['momentToDateString'];
                promoAttrs[attrKey] = transform ? transform(raw) : raw;
            }
        }

        return {
            plan_id: pid,
            event_id: eid,
            dim2_attrs: dim2Attrs,
            promo: {
                promo_id: promoId ?? '',
                attributes: promoAttrs,
                offer: offerPayload.params,
                products: buildUpliftProducts(filteredPpgs, cfg.payloadMetricKeys),
            },
        };
    }, []);

    /**
     * Core trigger with mode-specific validation:
     * - 'manual': hard validation with error notification
     * - 'auto': soft filter (silently skip PPGs without EDLP SRP)
     */
    const triggerUplift = useCallback(
        async (mode: TriggerMode = 'manual') => {
            const {
                ppgData: currentPpgData,
                isPending: pending,
                mutateAsync: mutate,
                promoFormValues: fv,
                upliftConfig: cfg,
                promoFields: fields,
                fieldStates: states,
            } = argsRef.current;
            const applicable =
                !!argsRef.current.promoType && cfg.applicablePromotionTypes.includes(argsRef.current.promoType);

            if (!applicable || currentPpgData.length === 0 || pending) return;

            // Skip if offer params aren't fully populated yet
            if (!isOfferComplete(fields, fv, states)) return;

            let targetPpgs: PPGRow[];

            if (mode === 'auto') {
                targetPpgs = filterPpgsForAutoTrigger(currentPpgData, cfg.autoTriggerFilterKey);
                if (targetPpgs.length === 0) return;
            } else {
                targetPpgs = currentPpgData;
            }

            const payload = buildPayload(targetPpgs);
            if (!payload) return;

            trackTpoGenEvent(TPOGEN_SIMULATE_STARTED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                plan_id: argsRef.current.planId,
                scope: 'promo',
            });
            const startedAt = performance.now();

            try {
                const response = await mutate(payload);
                applyUpliftResponse(response);
                trackTpoGenEvent(TPOGEN_SIMULATE_COMPLETED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    plan_id: argsRef.current.planId,
                    scope: 'promo',
                    duration_ms: Math.round(performance.now() - startedAt),
                    result_summary: JSON.stringify({ product_count: response.results?.length ?? 0 }),
                });
            } catch (error) {
                trackTpoGenEvent(TPOGEN_SIMULATE_FAILED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_PROMO, {
                    plan_id: argsRef.current.planId,
                    scope: 'promo',
                    failure_reason: (error as { response?: { data?: { message?: string } }; message?: string })
                        ?.response?.data?.message ?? (error as Error)?.message,
                    duration_ms: Math.round(performance.now() - startedAt),
                });
                // Error also handled in mutation hook (notification)
            }
        },
        [buildPayload, applyUpliftResponse],
    );

    /** Exposed to UI for the manual "Refresh Uplift" button */
    const triggerUpliftManual = useCallback(async () => {
        await triggerUplift('manual');
    }, [triggerUplift]);

    /**
     * Pre-save step: validates, calls API, applies response, then returns
     * true (proceed with save) or false (abort save).
     */
    const triggerUpliftBeforeSave = useCallback(async (): Promise<boolean> => {
        // Cancel any debounced auto-trigger so it can't fire a duplicate call right after save.
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        const {
            ppgData: currentPpgData,
            promoFormValues: fv,
            mutateAsync: mutate,
            upliftConfig: cfg,
            promoFields: fields,
            fieldStates: states,
        } = argsRef.current;
        const applicable =
            !!argsRef.current.promoType && upliftConfig.applicablePromotionTypes.includes(argsRef.current.promoType);

        if (!applicable || currentPpgData.length === 0) return true;

        if (!isOfferComplete(fields, fv, states)) return true;

        const payload = buildPayload(currentPpgData);
        if (!payload) return true;

        try {
            const response = await mutate(payload);
            applyUpliftResponse(response);
            return true;
        } catch {
            return false;
        }
    }, [buildPayload, applyUpliftResponse]);

    /** Auto-trigger after recommended values fetch (soft filter, no error) */
    const triggerUpliftAuto = useCallback(async () => {
        await triggerUplift('auto');
    }, [triggerUplift]);

    /** Debounced auto-trigger (500ms) for offer field changes */
    const triggerUpliftDebounced = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            triggerUpliftAuto();
        }, 500);
    }, [triggerUpliftAuto]);

    return {
        triggerUplift: triggerUpliftManual,
        triggerUpliftBeforeSave,
        triggerUpliftAuto,
        triggerUpliftDebounced,
        isUpliftApplicable,
    };
};

export default useUpliftTrigger;
